import EmptyLienWaiverIcon from "Assets/Img/EmptyLienWaiverIcon";
import EmptyState from "Components/Common/EmptyState/EmptyState";
import Table, { Column } from "Components/Table/Table";

interface LienWaiver {
  signedAt: string | null;
  invitedAt: string | null;
}

interface LienItem {
  id: string;
  subContractorName: string;
  totalAmount: { amount: number; currency: { symbol: string } };
  lienWaiver: LienWaiver | null;
}

export interface DrawGroup {
  lienItems: LienItem[];
}

interface Props {
  lienItemsData: Record<string, DrawGroup>;
}

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function allSigned(items: LienItem[]) {
  return items.length > 0 && items.every((i) => i.lienWaiver?.signedAt);
}

type FlatRow =
  | { _type: "group"; label: string }
  | { _type: "item"; drawNum: string; item: LienItem };

export default function LienWaiverTable({ lienItemsData }: Props) {
  const drawNumbers = Object.keys(lienItemsData).sort(
    (a, b) => Number(b) - Number(a),
  );

  if (drawNumbers.length === 0) {
    return (
      <EmptyState
        icon={<EmptyLienWaiverIcon />}
        title="No Lien Waivers Yet"
        subtitle="Lien waivers from contractors and subcontractors will appear here
    once they have been submitted for this draw."
      />
    );
  }

  const flatRows: FlatRow[] = drawNumbers.flatMap((drawNum) => {
    const { lienItems } = lienItemsData[drawNum];
    const label = allSigned(lienItems)
      ? `Draw #${drawNum} — All Received`
      : `Draw #${drawNum} — Current`;
    return [
      { _type: "group" as const, label },
      ...lienItems.map((item) => ({ _type: "item" as const, drawNum, item })),
    ];
  });

  const columns: Column<FlatRow>[] = [
    {
      header: "Draw",
      render: (row) => (row._type === "group" ? row.label : row.drawNum),
    },
    {
      header: "Vendor",
      render: (row) =>
        row._type === "item" ? row.item.subContractorName : null,
    },
    {
      header: "Amount",
      render: (row) => {
        if (row._type !== "item") return null;
        const { symbol } = row.item.totalAmount.currency;
        return `${symbol}${row.item.totalAmount.amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      },
    },
    {
      header: "Waiver Status",
      render: (row) => {
        if (row._type !== "item") return null;
        const signed = !!row.item.lienWaiver?.signedAt;
        return (
          <span className={signed ? "lw-signed" : "lw-pending"}>
            {signed ? "✓ Signed" : "⏳ Pending"}
          </span>
        );
      },
    },
    {
      header: "Date",
      render: (row) => {
        if (row._type !== "item") return null;
        const { signedAt, invitedAt } = row.item.lienWaiver ?? {};
        if (signedAt) return formatDate(signedAt);
        if (invitedAt) return `Sent ${formatDate(invitedAt)}`;
        return "—";
      },
    },
  ];

  return (
    <Table
      tableClassName="lien-table"
      data={flatRows}
      rowClassName={(row) => (row._type === "group" ? "draw-group" : "")}
      columns={columns}
    />
  );
}
